import React, { useState, useEffect, useCallback } from 'react';
import { DollarSign, Plus, Save, Edit2, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import { Switch } from '../../ui/switch';
import { useAdminSettings } from '../../../hooks/useAdminSettings';

const TierManagement = () => {
  const { apiCall, loading, setLoading, showSuccess, showError } = useAdminSettings();
  const [tiers, setTiers] = useState([]);
  const [newTier, setNewTier] = useState({ name: '', price: 0, features: '', commission_rate: 0, can_post: true });
  const [editingTier, setEditingTier] = useState(null);

  const fetchTiers = useCallback(async () => {
    try {
      const data = await apiCall('get', '/api/admin/settings/tiers');
      setTiers(data.tiers || []);
    } catch (error) {
      console.error('Error fetching tiers:', error);
    }
  }, [apiCall]);

  useEffect(() => {
    fetchTiers();
  }, [fetchTiers]);

  const handleSaveTier = async (tier) => {
    setLoading(true);
    try {
      const tierData = {
        name: tier.name,
        price: parseFloat(tier.price),
        interval: tier.price === 0 ? 'forever' : 'month',
        features: typeof tier.features === 'string' ? tier.features.split(',').map(f => f.trim()) : tier.features,
        can_post: tier.can_post,
        commission_rate: parseFloat(tier.commission_rate) || 0,
        is_active: true
      };

      if (editingTier) {
        await apiCall('put', `/api/admin/settings/tiers/${tier.id}`, tierData);
        showSuccess('Tier updated successfully');
      } else {
        await apiCall('post', '/api/admin/settings/tiers', tierData);
        showSuccess('Tier created successfully');
      }
      
      setEditingTier(null);
      setNewTier({ name: '', price: 0, features: '', commission_rate: 0, can_post: true });
      fetchTiers();
    } catch (error) {
      showError(error.response?.data?.detail || 'Failed to save tier');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTier = async (tierId) => {
    if (!window.confirm('Are you sure you want to delete this tier?')) return;
    
    try {
      await apiCall('delete', `/api/admin/settings/tiers/${tierId}`);
      showSuccess('Tier deleted successfully');
      fetchTiers();
    } catch (error) {
      showError(error.response?.data?.detail || 'Failed to delete tier');
    }
  };

  return (
    <div className="space-y-6">
      {/* Add/Edit Tier Form */}
      <Card className="bg-gray-900 border-gray-800">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Plus className="w-5 h-5" />
            {editingTier ? 'Edit Tier' : 'Create New Tier'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-gray-400">Tier Name</label>
              <Input
                value={editingTier?.name || newTier.name}
                onChange={(e) => editingTier 
                  ? setEditingTier({...editingTier, name: e.target.value})
                  : setNewTier({...newTier, name: e.target.value})
                }
                placeholder="e.g., Premium Plus"
                className="bg-gray-800 border-gray-700 text-white"
              />
            </div>
            <div>
              <label className="text-sm text-gray-400">Price ($/month, 0 for free)</label>
              <Input
                type="number"
                value={editingTier?.price ?? newTier.price}
                onChange={(e) => editingTier
                  ? setEditingTier({...editingTier, price: e.target.value})
                  : setNewTier({...newTier, price: e.target.value})
                }
                placeholder="0"
                className="bg-gray-800 border-gray-700 text-white"
              />
            </div>
            <div>
              <label className="text-sm text-gray-400">Commission Rate (%)</label>
              <Input
                type="number"
                value={editingTier?.commission_rate ?? newTier.commission_rate}
                onChange={(e) => editingTier
                  ? setEditingTier({...editingTier, commission_rate: e.target.value})
                  : setNewTier({...newTier, commission_rate: e.target.value})
                }
                placeholder="0"
                className="bg-gray-800 border-gray-700 text-white"
              />
            </div>
            <div className="flex items-center gap-3">
              <label className="text-sm text-gray-400">Can Upload Content</label>
              <Switch
                checked={editingTier?.can_post ?? newTier.can_post}
                onCheckedChange={(checked) => editingTier
                  ? setEditingTier({...editingTier, can_post: checked})
                  : setNewTier({...newTier, can_post: checked})
                }
              />
            </div>
          </div>
          <div>
            <label className="text-sm text-gray-400">Features (comma-separated)</label>
            <Input
              value={editingTier?.features 
                ? (Array.isArray(editingTier.features) ? editingTier.features.join(', ') : editingTier.features)
                : newTier.features
              }
              onChange={(e) => editingTier
                ? setEditingTier({...editingTier, features: e.target.value})
                : setNewTier({...newTier, features: e.target.value})
              }
              placeholder="Feature 1, Feature 2, Feature 3"
              className="bg-gray-800 border-gray-700 text-white"
            />
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => handleSaveTier(editingTier || newTier)}
              disabled={loading}
              className="bg-yellow-500 hover:bg-yellow-600 text-black"
            >
              <Save className="w-4 h-4 mr-2" />
              {editingTier ? 'Update Tier' : 'Create Tier'}
            </Button>
            {editingTier && (
              <Button
                onClick={() => setEditingTier(null)}
                variant="outline"
                className="border-gray-600"
              >
                Cancel
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Existing Tiers */}
      <Card className="bg-gray-900 border-gray-800">
        <CardHeader>
          <CardTitle className="text-white">Current Tiers ({tiers.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {tiers.map((tier) => (
              <div key={tier.id} className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 p-4 bg-gray-800/50 rounded-lg border border-gray-700">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-semibold text-white">{tier.name}</h4>
                    <Badge className={tier.commission_rate > 0 ? 'bg-emerald-500/20 text-emerald-400' : 'bg-gray-600'}>
                      {tier.price === 0 ? 'Free' : `$${tier.price}/mo`}
                    </Badge>
                    {tier.commission_rate > 0 && (
                      <Badge className="bg-purple-500/20 text-purple-400">
                        {tier.commission_rate}% commission
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-gray-400">
                    {tier.user_count || 0} users • {tier.can_post ? 'Can upload' : 'View only'}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Features: {Array.isArray(tier.features) ? tier.features.slice(0, 3).join(', ') : tier.features}
                    {tier.features?.length > 3 && '...'}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => setEditingTier(tier)}
                    size="sm"
                    variant="outline"
                    className="border-gray-600"
                  >
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  {!['free', 'standard', 'contract'].includes(tier.id) && (
                    <Button
                      onClick={() => handleDeleteTier(tier.id)}
                      size="sm"
                      variant="outline"
                      className="border-red-500 text-red-400 hover:bg-red-500/10"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TierManagement;
